/**
 * TenantEtaCard — Iter-91 · Resident portal live ETA.
 *
 * Reads from `GET /api/operations/tenant-eta` which the backend
 * property-scopes automatically for tenant callers. Polls every 30 s in
 * sync with the Ops map. When no route is active, shows the next scheduled
 * pickup date instead so the card is never empty.
 */
import { useEffect, useState, useRef } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Truck, Clock, Calendar, Activity } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 30000;

export default function TenantEtaCard() {
  const [data, setData] = useState(null);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const r = await fetch(`${API}/api/operations/tenant-eta`, { credentials: 'include' });
      if (!r.ok) return;
      const d = await r.json();
      setData(d);
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, []);

  if (!data) return null;

  if (!data.active) {
    return (
      <Card className="bg-zinc-900 border border-zinc-800 p-4" data-testid="tenant-eta-card">
        <div className="flex items-center gap-2 mb-1">
          <Calendar className="h-4 w-4 text-[#C9A962]" />
          <p className="text-[10px] uppercase tracking-widest text-[#C9A962] font-bold">Next Valet Pickup</p>
        </div>
        <p className="text-lg font-black text-white leading-none mt-1" data-testid="tenant-eta-next-date">
          {data.next_pickup_date || '—'}
        </p>
        <p className="text-[11px] text-zinc-400 mt-1">
          No route is active for your property right now. We&apos;ll update this card the moment your crew heads out.
        </p>
      </Card>
    );
  }

  const barPct = Math.max(0, Math.min(100, Math.round(((data.current_stop || 0) / Math.max(data.total_stops || 1, 1)) * 100)));
  const delayed = !!data.delayed;

  return (
    <Card
      className="bg-zinc-900 border-2 p-4"
      style={{ borderColor: delayed ? 'rgba(239,68,68,0.5)' : 'rgba(16,185,129,0.4)' }}
      data-testid="tenant-eta-card"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Truck className={`h-4 w-4 ${delayed ? 'text-red-400' : 'text-emerald-400'}`} />
          <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: delayed ? '#f87171' : '#34d399' }}>
            Live · Your Valet Route
          </p>
        </div>
        <Badge className={`text-[10px] ${delayed ? 'bg-red-700' : 'bg-emerald-700'} text-white`}>
          {delayed ? 'Delayed' : 'On-track'}
        </Badge>
      </div>
      <p className="text-lg font-black text-white leading-tight" data-testid="tenant-eta-window">
        {data.message}
      </p>
      <p className="text-[11px] text-zinc-400 mt-1">
        Currently at stop <b className="text-white">{data.current_stop}</b> of <b className="text-white">{data.total_stops}</b>
        {typeof data.eta_minutes === 'number' && (
          <> · <Clock className="inline h-3 w-3 -mt-0.5" /> ~{data.eta_minutes} min away</>
        )}
      </p>
      <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden mt-3">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${barPct}%`, backgroundColor: delayed ? '#ef4444' : '#10b981' }}
          data-testid="tenant-eta-progress"
        />
      </div>
      <p className="text-[10px] text-zinc-500 mt-2 flex items-center gap-1">
        <Activity className="h-3 w-3" /> Track live updates — refreshes every 30 s.
      </p>
    </Card>
  );
}
